/**
 * PublicProfilePage
 *
 * Read-only public profile view accessible via share token.
 * Shows calendar and workload based on profile visibility settings.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CalendarDays, BarChart3, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import clsx from 'clsx';
import { getPublicProfile, getPublicProfileTasks, type ProfileSettingsUI } from '@/db/profiles';
import { convertTaskFromSupabase, type Task, type TaskDoc } from '@/db';
import { isCompletedStatus, normalizeStatus, TASK_STATUS } from '@/constants/taskStatuses';
import { ProfileCalendar } from './ProfileCalendar';
import { ProfileWorkload } from './ProfileWorkload';
import type { WorkloadStats } from '@/hooks/useProfile';

type PublicTab = 'calendario' | 'workload';

export function PublicProfilePage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [profile, setProfile] = useState<ProfileSettingsUI | null>(null);
  const [tasks, setTasks] = useState<(Task & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PublicTab>('calendario');

  useEffect(() => {
    if (!shareToken) {
      setError('Token non valido');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const profileData = await getPublicProfile(shareToken);
        if (!profileData) {
          if (!cancelled) {
            setError('Profilo non trovato o condivisione disabilitata');
          }
          return;
        }

        if (!cancelled) {
          setProfile(profileData);
        }

        // Load tasks for this user
        const rawTasks = await getPublicProfileTasks(profileData.userId);
        if (!cancelled) {
          const converted = (rawTasks as TaskDoc[]).map(convertTaskFromSupabase);
          setTasks(converted);
        }
      } catch {
        if (!cancelled) {
          setError('Errore nel caricamento del profilo');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [shareToken]);

  // Compute workload stats
  const workloadStats = useMemo((): WorkloadStats => {
    const now = Date.now();
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter((t) => isCompletedStatus(t.status)).length;
    const inProgressTasks = tasks.filter(
      (t) => normalizeStatus(t.status) === TASK_STATUS.IN_PROGRESS
    ).length;
    const blockedTasks = tasks.filter(
      (t) => normalizeStatus(t.status) === TASK_STATUS.BLOCKED
    ).length;
    const openTasks = tasks.filter((t) => {
      const s = normalizeStatus(t.status);
      return s === TASK_STATUS.OPEN || s === TASK_STATUS.TODO;
    }).length;
    const overdueTasks = tasks.filter((t) => {
      if (!t.dueDate) return false;
      return t.dueDate < now && !isCompletedStatus(t.status);
    }).length;
    const totalTimeEstimated = tasks.reduce((sum, t) => sum + (t.timeEstimated || 0), 0);
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return {
      totalTasks,
      completedTasks,
      inProgressTasks,
      overdueTasks,
      openTasks,
      blockedTasks,
      totalTimeEstimated,
      completionRate,
    };
  }, [tasks]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-primary gradient-radial">
        <div className="flex flex-col items-center gap-3 animate-fade-in">
          <Loader2 className="w-6 h-6 text-accent-violet animate-spin" />
          <p className="text-[12px] text-text-tertiary">Caricamento profilo...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-primary gradient-radial">
        <div className="text-center animate-fade-in max-w-sm">
          <div className="w-14 h-14 rounded-2xl bg-accent-rose-dim flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-accent-rose" />
          </div>
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            Profilo non disponibile
          </h2>
          <p className="text-sm text-text-tertiary mb-4">
            {error || 'Il profilo richiesto non e stato trovato.'}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-accent-violet text-white text-sm font-medium hover:bg-accent-violet-light transition-colors shadow-lg shadow-accent-violet/20"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna al login
          </Link>
        </div>
      </div>
    );
  }

  const showCalendar = profile.publicShowCalendar;

  const tabs: { id: PublicTab; label: string; icon: typeof CalendarDays; show: boolean }[] = [
    { id: 'calendario', label: 'Calendario', icon: CalendarDays, show: showCalendar },
    { id: 'workload', label: 'Workload', icon: BarChart3, show: true },
  ];

  const visibleTabs = tabs.filter((t) => t.show);

  // If current tab is not visible, switch to first visible
  if (!visibleTabs.find((t) => t.id === activeTab) && visibleTabs.length > 0) {
    setActiveTab(visibleTabs[0].id);
  }

  return (
    <div className="min-h-screen bg-surface-primary">
      {/* Public Header */}
      <div className="border-b border-border-primary bg-surface-secondary/30">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-linear-to-br from-accent-violet to-accent-blue flex items-center justify-center shadow-lg shadow-accent-violet/20">
              <span className="text-sm font-bold text-white">Z</span>
            </div>
            <div>
              <h1 className="text-[14px] font-semibold text-text-primary">
                Profilo pubblico
              </h1>
              <p className="text-[10px] text-text-quaternary">
                Condiviso tramite Zenda
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border-primary bg-surface-secondary/20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-center gap-1">
            {visibleTabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={clsx(
                  'flex items-center gap-2 px-3.5 py-2.5 text-[12px] font-medium border-b-2 transition-all -mb-px',
                  activeTab === id
                    ? 'text-accent-violet border-accent-violet'
                    : 'text-text-tertiary border-transparent hover:text-text-secondary hover:border-border-secondary'
                )}
              >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        {activeTab === 'calendario' && showCalendar && (
          <ProfileCalendar tasks={tasks} />
        )}
        {activeTab === 'workload' && (
          <ProfileWorkload stats={workloadStats} tasks={tasks} />
        )}
      </div>
    </div>
  );
}
